import { ReactElement, useEffect, useState } from 'react'

import { Button, notification, Typography } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'

import { COGNITO_ACCESS_TOKEN } from 'const/localStorage'
import { SegmentEventName } from 'const/monitoring/segment'
import { useUserContext } from 'context/UserContext'
import {
    type AccessToken,
    createStorableAccessToken,
} from 'features/Auth/accessToken'
import { signOut } from 'features/Auth/authService'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import CobaltJSLogo from 'images/logo/CobaltJSLogo'
import { getPath } from 'routing/pagePaths'

import AccessTokenForm from './Forms/AccessTokenForm'
import AddPhoneNumberForm from './Forms/AddPhoneNumberForm'
import VerifyPhoneNumberForm from './Forms/VerifyPhoneNumberForm'
import * as mfaSetupStyles from './styles.scss'

type SetUpPhoneNumberPageStep = 'accessToken' | 'addPhoneNumber' | 'verify'

const SetUpPhoneNumberPage = (): ReactElement => {
    const { t } = useTranslation('account')
    const [accessToken, setAccessToken] = useState<AccessToken>()
    const navigate = useNavigate()
    const [validPhoneNumber, setValidPhoneNumber] = useState<string>()
    const { setMfaConfigured, userMfaSettings } = useUserContext()

    const now = new Date().getTime()
    const haveValidToken = accessToken?.value && accessToken?.ttl >= now
    const { trackEvent } = useAnalytics()

    let currentStep: SetUpPhoneNumberPageStep = 'verify'
    if (!haveValidToken) {
        currentStep = 'accessToken'
    } else if (!validPhoneNumber) {
        currentStep = 'addPhoneNumber'
    } else {
        currentStep = 'verify'
    }

    useEffect(() => {
        if (currentStep === 'addPhoneNumber') {
            trackEvent(SegmentEventName.MFA_ADD_PHONE_NUMBER_STARTED, {
                text: 'MFA - Add Phone Number Started',
            })
        } else if (currentStep === 'verify') {
            trackEvent(SegmentEventName.MFA_VERIFY_PHONE_NUMBER_STARTED, {
                text: 'MFA - Verify Phone Number Started',
            })
        }
    }, [currentStep, trackEvent])

    const updateAccessToken = (newAccessToken: string): void => {
        const token = createStorableAccessToken(newAccessToken)

        localStorage.setItem(COGNITO_ACCESS_TOKEN, JSON.stringify(token))
        setAccessToken(token)
    }

    useEffect(() => {
        const storedData = localStorage.getItem(COGNITO_ACCESS_TOKEN)
        if (storedData) {
            const token = JSON.parse(storedData) as AccessToken
            if (token.ttl >= new Date().getTime()) {
                setAccessToken(token)
            }
        }
    }, [])

    const logOut = (): void => {
        navigate(getPath('authPage'))
        signOut()
    }

    function setMfaConfigurationComplete(): void {
        setMfaConfigured()
        notification.config({
            top: 10,
            duration: 5,
            placement: 'top',
        })
        notification.success({
            message: t(
                'account:SetUpPhoneNumberPage.successMsg',
                'Your phone number has been successfully verified! Two-factor authentication is now active.'
            ),
            style: {
                width: 'auto',
                fontSize: 14,
            },
        })
    }

    if (userMfaSettings?.needsToSetUpMfa === false) {
        return <Navigate to="/" />
    }

    return (
        <div className={mfaSetupStyles.container}>
            <div className={mfaSetupStyles.logo}>
                <CobaltJSLogo className={mfaSetupStyles.authLogo} />
            </div>
            <div
                className={classNames(
                    mfaSetupStyles['form-container'],
                    mfaSetupStyles.form
                )}
            >
                {currentStep === 'accessToken' && (
                    <AccessTokenForm
                        onAccessTokenAcquired={updateAccessToken}
                    />
                )}
                {currentStep === 'addPhoneNumber' && accessToken && (
                    <AddPhoneNumberForm
                        accessToken={accessToken.value}
                        onPhoneNumberUpdated={(phoneNumber) =>
                            setValidPhoneNumber(phoneNumber)
                        }
                    />
                )}
                {currentStep === 'verify' &&
                    accessToken &&
                    validPhoneNumber && (
                        <VerifyPhoneNumberForm
                            accessToken={accessToken.value}
                            phoneNumberToVerify={validPhoneNumber}
                            onPhoneNumberVerified={() =>
                                setMfaConfigurationComplete()
                            }
                            onEditPhoneNumberRequested={() =>
                                setValidPhoneNumber(undefined)
                            }
                        />
                    )}
                <div className={mfaSetupStyles.footer}>
                    <Typography>
                        {t(
                            'mfa.setUpPhoneNumber.changeAccounts',
                            'Need to change accounts?'
                        )}{' '}
                        <Button
                            type="link"
                            onClick={logOut}
                            className={mfaSetupStyles.mfaLogoutLink}
                        >
                            {t('common:logOut', 'Log Out')}
                        </Button>
                    </Typography>
                </div>
            </div>
        </div>
    )
}

export default SetUpPhoneNumberPage
